import { useMemo, useRef, useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { getUserById, type Reply } from '@/lib/community';
import { CURRENT_USER_ID } from '@/lib/constants';
import { cn, formatRelativeTime } from '@/lib/format';
import UserAvatar from '@/components/feature/UserAvatar';

const EMOJIS = ['😀', '😂', '🥰', '👍', '🔥', '🍯', '🌿', '✨'];

interface ReplySectionProps {
  topicId: string;
  initialReplies: Reply[];
  onToast: (message: string) => void;
}

export default function ReplySection({ topicId, initialReplies, onToast }: ReplySectionProps) {
  const [list, setList] = useState<Reply[]>(
    () => [...initialReplies].sort((a, b) => +new Date(b.createdAt) - +new Date(a.createdAt)),
  );
  const [content, setContent] = useState('');
  const [error, setError] = useState('');
  const [showEmoji, setShowEmoji] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const formRef = useRef<HTMLDivElement>(null);

  const currentUser = getUserById(CURRENT_USER_ID);
  const charLeft = 500 - content.length;

  const jumpToForm = () => {
    setExpanded(true);
    setTimeout(() => {
      formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      formRef.current?.querySelector('textarea')?.focus();
    }, 50);
  };

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (!content.trim()) {
      setError('回复内容不能为空，说点什么吧～');
      return;
    }
    if (content.length > 500) {
      setError('回复内容不能超过 500 个字符。');
      return;
    }
    const newReply: Reply = {
      id: `new-${Date.now()}`,
      topicId,
      userId: CURRENT_USER_ID,
      floor: list.length + 1,
      likes: 0,
      createdAt: new Date().toISOString(),
      content: content.trim(),
    };
    setList((prev) => [newReply, ...prev]);
    setContent('');
    setError('');
    setShowEmoji(false);
    setExpanded(false);
    onToast('回复发表成功（前端演示，未存储到服务器）');
  };

  const emojiCount = useMemo(() => list.reduce((acc, r) => acc + r.likes, 0), [list]);

  return (
    <section className="mt-8">
      <div className="flex items-center justify-between gap-4 mb-4">
        <h2 className="text-lg md:text-xl font-extrabold text-foreground-900">
          全部回复 <span className="text-foreground-400 font-label">{list.length}</span>
        </h2>
        <span className="text-xs text-foreground-400">按时间倒序 · 共 {emojiCount} 次点赞</span>
      </div>

      {/* Reply form */}
      <div ref={formRef} className="bg-background-50 border border-background-200 rounded-lg p-4 md:p-5">
        <div className="flex gap-3">
          <UserAvatar src={currentUser?.avatar ?? ''} alt={currentUser?.name ?? '我'} size="sm" className="hidden sm:inline-flex" />
          <form onSubmit={handleSubmit} className="flex-1 min-w-0">
            <textarea
              value={content}
              onChange={(e) => { setContent(e.target.value); setError(''); }}
              onFocus={() => setExpanded(true)}
              rows={expanded ? 4 : 2}
              maxLength={500}
              placeholder="友善发言，分享你的看法…（前端演示，不会真正提交）"
              className="w-full resize-none rounded-md border border-background-300 bg-background-50 px-3.5 py-3 text-sm text-foreground-900 placeholder:text-foreground-400 outline-none focus:border-primary-400 transition-colors"
            />

            {showEmoji && (
              <div className="mt-2 flex flex-wrap gap-1.5 rounded-md border border-background-200 bg-background-100 p-2">
                {EMOJIS.map((emoji) => (
                  <button
                    key={emoji}
                    type="button"
                    onClick={() => setContent((c) => `${c}${emoji}`)}
                    className="w-8 h-8 rounded-md hover:bg-background-200 text-lg flex items-center justify-center cursor-pointer"
                  >
                    {emoji}
                  </button>
                ))}
              </div>
            )}

            <div className="mt-2.5 flex items-center justify-between gap-3 flex-wrap">
              <div className="flex items-center gap-1">
                <button
                  type="button"
                  onClick={() => setShowEmoji((v) => !v)}
                  aria-label="插入表情"
                  className={cn(
                    'inline-flex items-center gap-1 rounded-md px-2.5 py-1.5 text-xs font-label font-bold transition-colors cursor-pointer whitespace-nowrap',
                    showEmoji ? 'bg-primary-100 text-primary-700' : 'text-foreground-500 hover:bg-background-100',
                  )}
                >
                  <i className="ri-emotion-happy-line" />
                  表情
                </button>
                <button
                  type="button"
                  onClick={() => onToast('图片上传为前端演示功能，暂未接入真实存储')}
                  className="inline-flex items-center gap-1 rounded-md px-2.5 py-1.5 text-xs font-label font-bold text-foreground-500 hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-image-add-line" />
                  图片
                </button>
                <span className={cn('text-[11px] pl-1', charLeft < 40 ? 'text-accent-600' : 'text-foreground-400')}>
                  {content.length}/500
                </span>
              </div>
              <button
                type="submit"
                className="inline-flex items-center gap-1.5 rounded-md bg-primary-500 hover:bg-primary-600 text-background-50 text-sm font-label font-bold px-4 py-2 whitespace-nowrap transition-colors cursor-pointer"
              >
                <i className="ri-send-plane-fill" />
                发表回复
              </button>
            </div>

            {error && (
              <p className="mt-2 text-xs text-accent-600 flex items-center gap-1">
                <i className="ri-error-warning-line" />
                {error}
              </p>
            )}
          </form>
        </div>
      </div>

      {/* Reply list */}
      <div className="mt-5 flex flex-col gap-3">
        {list.length > 0 ? (
          list.map((reply) => {
            const user = getUserById(reply.userId);
            return (
              <article key={reply.id} className="flex gap-3 p-4 md:p-5 bg-background-50 border border-background-200 rounded-lg">
                <Link to={`/user/${reply.userId}`} className="cursor-pointer shrink-0">
                  <UserAvatar src={user?.avatar ?? ''} alt={user?.name ?? '用户'} size="md" />
                </Link>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <Link to={`/user/${reply.userId}`} className="text-sm font-bold text-foreground-900 hover:text-primary-700 transition-colors cursor-pointer">
                      {user?.name ?? '匿名用户'}
                    </Link>
                    {user?.badges[0] && (
                      <span className="rounded-full bg-secondary-100 text-secondary-700 px-2 py-0.5 text-[11px] font-label font-bold">
                        {user.badges[0]}
                      </span>
                    )}
                    <span className="text-xs text-foreground-400">{reply.floor} 楼</span>
                    <span className="text-xs text-foreground-400">· {formatRelativeTime(reply.createdAt)}</span>
                  </div>
                  <p className="mt-2 text-sm text-foreground-700 leading-relaxed">{reply.content}</p>
                  <div className="mt-2 flex items-center gap-4 text-xs text-foreground-500">
                    <span className="inline-flex items-center gap-1">
                      <i className="ri-thumb-up-line" />
                      {reply.likes}
                    </span>
                    <span className="inline-flex items-center gap-1 cursor-pointer hover:text-primary-700 transition-colors">
                      <i className="ri-reply-line" />
                      回复
                    </span>
                  </div>
                </div>
              </article>
            );
          })
        ) : (
          <div className="flex flex-col items-center justify-center text-center py-12 bg-background-50 border border-dashed border-background-300 rounded-lg">
            <i className="ri-chat-new-line text-3xl text-foreground-300 mb-3" />
            <p className="text-sm font-bold text-foreground-800">还没有人回复</p>
            <p className="text-xs text-foreground-400 mt-1">来抢第一个沙发，说说你的想法</p>
          </div>
        )}
      </div>

      {/* Mobile floating reply button */}
      <button
        type="button"
        onClick={jumpToForm}
        className="md:hidden fixed bottom-5 right-5 z-40 inline-flex items-center gap-1.5 rounded-full bg-primary-500 text-background-50 text-sm font-label font-bold px-4 py-3 shadow-sm cursor-pointer"
      >
        <i className="ri-edit-2-line" />
        写回复
      </button>
    </section>
  );
}